import {
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react';

import type { DataTableColumnSettingsModalRef } from '../components/Toolbar/ColumnSettings/column-settings-types.js';
import {
  getToolbarConfig,
  type DataTableToolbarItemProps,
  type DataTableToolbarProps,
  type DataTableToolbarSlots,
} from '../components/Toolbar/DataTableToolbar.js';
import type { DataTableColumnSettingsConfig } from '../public.api.js';

/**
 * Processes the toolbar slot.
 * @param toolbarProps - toolbar props extracted from datatable slots
 * @returns processed toolbar slot data
 * @internal
 */
export function useToolbarSlot(toolbarProps: DataTableToolbarProps | null): {
  toolbarSlots: DataTableToolbarSlots;
  toolbarContent: ReactNode;
  toolbarItemProps: DataTableToolbarItemProps;
  toolbarRemainingProps: Omit<DataTableToolbarProps, 'children'>;
  openColumnSettings: (columnSettings?: DataTableColumnSettingsConfig) => void;
  columnSettingsModalRef: RefObject<DataTableColumnSettingsModalRef>;
} {
  const { children: toolbarContent, ...toolbarRemainingProps } =
    toolbarProps ?? {};
  const { slots: toolbarSlots, toolbarItemProps } = useMemo(
    () => getToolbarConfig(toolbarContent),
    [toolbarContent],
  );

  const columnSettingsModalRef = useRef<DataTableColumnSettingsModalRef>(null);

  // Create a stable callback for opening the column settings modal
  // that always uses the latest ref value
  const openColumnSettings = useCallback(
    (columnSettings?: DataTableColumnSettingsConfig) => {
      columnSettingsModalRef.current?.openColumnSettings(columnSettings);
    },
    [],
  );

  return {
    toolbarSlots,
    toolbarContent,
    toolbarItemProps,
    toolbarRemainingProps,
    openColumnSettings,
    columnSettingsModalRef,
  };
}
